import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Shield, Eye, Settings, 
  CheckCircle, Zap, BarChart3, Brain, Users, 
  Scale, MessageSquare, TrendingUp, Target
} from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';

interface AIEthicsFunction {
  id: string;
  name: string;
  description: string;
  icon: any;
  color: string;
  asis: number;
  target: number;
  subcategories: {
    id: string;
    name: string;
    description: string;
    asis: number;
    target: number;
    outcomes: string[];
  }[];
}

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  icon: any;
  color: string;
}

interface AIEthiquePresentationProps {
  isOpen: boolean;
  onClose: () => void;
}

const aiEthicsFunctions: AIEthicsFunction[] = [
  {
    id: 'govern',
    name: 'GOUVERNER',
    description: 'Établir des politiques, des responsabilités et des processus pour gérer les risques liés à l\'IA',
    icon: Shield,
    color: '#0066FF',
    asis: 75,
    target: 90,
    subcategories: [
      {
        id: 'govern-1',
        name: 'Stratégie & Politiques IA',
        description: 'Définition des principes directeurs et des cadres éthiques pour l\'IA',
        asis: 80,
        target: 95,
        outcomes: ['Politique d\'IA Éthique définie', 'Comité de gouvernance IA établi', 'Charte éthique BZ']
      },
      {
        id: 'govern-2',
        name: 'Rôles & Responsabilités',
        description: 'Attribution claire des rôles et des responsabilités pour le cycle de vie de l\'IA',
        asis: 70,
        target: 85,
        outcomes: ['Matrice RACI pour l\'IA', 'Formation des équipes', 'Accountability claire']
      },
      {
        id: 'govern-3',
        name: 'Auditabilité & Conformité',
        description: 'Mise en place de mécanismes d\'audit et de conformité réglementaire',
        asis: 75,
        target: 90,
        outcomes: ['Procédures d\'audit IA', 'Conformité RGPD/LPRPDE', 'Traçabilité complète']
      }
    ]
  },
  {
    id: 'map',
    name: 'CARTOGRAPHIER',
    description: 'Identifier les contextes, les capacités et les risques de l\'IA',
    icon: BarChart3,
    color: '#003366',
    asis: 78,
    target: 88,
    subcategories: [
      {
        id: 'map-1',
        name: 'Identification des Cas d\'Usage',
        description: 'Analyse des applications potentielles de l\'IA et de leurs impacts',
        asis: 85,
        target: 95,
        outcomes: ['Inventaire des systèmes IA', 'Analyse d\'impact éthique', 'ROI évalué']
      },
      {
        id: 'map-2',
        name: 'Évaluation des Risques',
        description: 'Identification et catégorisation des risques spécifiques à chaque système IA',
        asis: 70,
        target: 85,
        outcomes: ['Registre des risques IA', 'Méthodologie d\'évaluation', 'Priorisation des risques']
      },
      {
        id: 'map-3',
        name: 'Analyse des Données',
        description: 'Compréhension des sources de données, de leur qualité et de leurs biais potentiels',
        asis: 80,
        target: 90,
        outcomes: ['Cartographie des données IA', 'Rapports de qualité', 'Détection de biais']
      }
    ]
  },
  {
    id: 'measure',
    name: 'MESURER',
    description: 'Quantifier, surveiller et analyser les risques de l\'IA',
    icon: Zap,
    color: '#F39C12',
    asis: 72,
    target: 85,
    subcategories: [
      {
        id: 'measure-1',
        name: 'Métriques de Performance Éthique',
        description: 'Définition et suivi des indicateurs de performance liés à l\'éthique de l\'IA',
        asis: 65,
        target: 80,
        outcomes: ['Tableau de bord éthique IA', 'KPIs de biais et d\'équité', 'Métriques de transparence']
      },
      {
        id: 'measure-2',
        name: 'Surveillance Continue',
        description: 'Mise en place de systèmes de surveillance pour détecter les dérives et les anomalies',
        asis: 75,
        target: 90,
        outcomes: ['Alertes automatiques', 'Rapports de dérive de modèle', 'Monitoring 24/7']
      },
      {
        id: 'measure-3',
        name: 'Évaluation des Biais',
        description: 'Méthodes pour identifier, mesurer et atténuer les biais algorithmiques',
        asis: 70,
        target: 85,
        outcomes: ['Outils de détection de biais', 'Rapports d\'équité', 'Tests de robustesse']
      }
    ]
  },
  {
    id: 'manage',
    name: 'GÉRER',
    description: 'Mettre en œuvre des stratégies pour atténuer les risques de l\'IA',
    icon: Settings,
    color: '#27AE60',
    asis: 80,
    target: 92,
    subcategories: [
      {
        id: 'manage-1',
        name: 'Atténuation des Risques',
        description: 'Développement et application de stratégies pour réduire les risques identifiés',
        asis: 85,
        target: 95,
        outcomes: ['Plans d\'action pour les risques IA', 'Mise à jour des modèles', 'Contrôles préventifs']
      },
      {
        id: 'manage-2',
        name: 'Sécurité des Données & Modèles',
        description: 'Protection des données d\'entraînement et des modèles IA contre les menaces',
        asis: 90,
        target: 98,
        outcomes: ['Protocoles de sécurité IA', 'Tests de robustesse', 'Chiffrement des modèles']
      },
      {
        id: 'manage-3',
        name: 'Human-in-the-Loop',
        description: 'Intégration de la supervision humaine dans les processus de décision de l\'IA',
        asis: 85,
        target: 95,
        outcomes: ['Procédures de validation humaine', 'Interfaces de contrôle', 'Escalade automatique']
      }
    ]
  },
  {
    id: 'assure',
    name: 'ASSURER',
    description: 'Vérifier l\'efficacité des mesures de gestion des risques',
    icon: CheckCircle,
    color: '#8E44AD',
    asis: 68,
    target: 82,
    subcategories: [
      {
        id: 'assure-1',
        name: 'Tests & Validation',
        description: 'Réalisation de tests rigoureux pour valider la performance et l\'éthique des systèmes IA',
        asis: 70,
        target: 85,
        outcomes: ['Plans de test IA', 'Rapports de validation', 'Tests d\'intégration']
      },
      {
        id: 'assure-2',
        name: 'Audits Indépendants',
        description: 'Recours à des audits externes pour une évaluation objective',
        asis: 60,
        target: 80,
        outcomes: ['Rapports d\'audit externe', 'Certifications éthiques', 'Benchmarks externes']
      },
      {
        id: 'assure-3',
        name: 'Feedback & Amélioration',
        description: 'Collecte de retours d\'expérience pour l\'amélioration continue',
        asis: 75,
        target: 90,
        outcomes: ['Mécanismes de feedback utilisateur', 'Boucles d\'amélioration', 'Apprentissage continu']
      }
    ]
  },
  {
    id: 'communicate',
    name: 'COMMUNIQUER',
    description: 'Partager les informations sur les risques et les mesures prises avec les parties prenantes',
    icon: MessageSquare,
    color: '#3498DB',
    asis: 85,
    target: 95,
    subcategories: [
      {
        id: 'communicate-1',
        name: 'Transparence Client',
        description: 'Communication claire sur l\'utilisation de l\'IA et ses limites aux clients',
        asis: 90,
        target: 98,
        outcomes: ['Documentation client sur l\'IA', 'Sessions d\'information', 'Rapports de transparence']
      },
      {
        id: 'communicate-2',
        name: 'Rapports Internes',
        description: 'Diffusion des informations sur la gouvernance et les performances de l\'IA en interne',
        asis: 80,
        target: 90,
        outcomes: ['Tableaux de bord internes IA', 'Réunions de comité IA', 'Dashboards temps réel']
      },
      {
        id: 'communicate-3',
        name: 'Éducation & Sensibilisation',
        description: 'Formation des équipes et des parties prenantes sur les enjeux de l\'IA éthique',
        asis: 85,
        target: 95,
        outcomes: ['Programmes de formation IA', 'Guides de bonnes pratiques', 'Workshops éthique']
      }
    ]
  }
];

const createSlides = (setSelectedFunction: (func: AIEthicsFunction) => void): Slide[] => [
  {
    id: 'title',
    title: 'IA Éthique et Responsable',
    subtitle: 'L\'approche BZ pour une intelligence artificielle de confiance',
    icon: Brain,
    color: '#9B59B6',
    content: (
      <div className="text-center space-y-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="w-32 h-32 mx-auto bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center"
        >
          <Brain className="w-16 h-16 text-white" />
        </motion.div>
        
        <div className="space-y-4">
          <h1 className="text-4xl font-bold text-bz-dark">IA Éthique et Responsable</h1>
          <h2 className="text-2xl text-purple-600 font-semibold">L'approche BZ</h2>
          <p className="text-xl text-gray-600">Intelligence artificielle de confiance au service de l'excellence</p>
        </div>

        <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
            <div className="text-2xl font-bold text-purple-600">6</div>
            <div className="text-sm text-purple-800">Fonctions IA</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
            <div className="text-2xl font-bold text-blue-600">18</div>
            <div className="text-sm text-blue-800">Sous-catégories</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
            <div className="text-2xl font-bold text-green-600">100%</div>
            <div className="text-sm text-green-800">Humain décide</div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'spider-overview',
    title: 'Vue d\'Ensemble - Spider Chart IA',
    subtitle: 'Maturité des 6 Fonctions IA Éthique',
    icon: BarChart3,
    color: '#3498DB',
    content: (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-xl border-l-4 border-purple-500">
          <h3 className="text-xl font-bold text-purple-800 mb-4">🕸️ Maturité Globale IA Éthique</h3>
          <p className="text-gray-700">Évaluation ASIS vs Target pour les 6 fonctions principales de l'IA responsable</p>
        </div>

        {/* Spider Chart Fonctionnel */}
        <div className="bg-white rounded-lg p-8 shadow-md">
          <h4 className="text-lg font-semibold text-gray-800 mb-6 text-center">
            🕸️ Spider Chart IA Éthique - Maturité Globale
          </h4>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={aiEthicsFunctions.map(func => ({
                function: func.name,
                asis: func.asis,
                target: func.target,
                minRequired: 70
              }))}>
                <PolarGrid />
                <PolarAngleAxis dataKey="function" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar
                  name="ASIS"
                  dataKey="asis"
                  stroke="#9B59B6"
                  fill="#9B59B6"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
                <Radar
                  name="Target"
                  dataKey="target"
                  stroke="#27AE60"
                  fill="#27AE60"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
                <Radar
                  name="Minimum"
                  dataKey="minRequired"
                  stroke="#E74C3C"
                  fill="none"
                  strokeWidth={1}
                  strokeDasharray="5 5"
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded"></div>
              <span>ASIS</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Target</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 border border-red-500 rounded"></div>
              <span>Minimum (70%)</span>
            </div>
          </div>
        </div>

        {/* Functions Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {aiEthicsFunctions.map((func, index) => {
            const Icon = func.icon;
            return (
              <motion.div
                key={func.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setSelectedFunction(func)}
                className="bg-white p-4 rounded-lg shadow-sm border cursor-pointer hover:shadow-lg hover:border-purple-300 transition-all group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${func.color}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: func.color }} />
                  </div>
                  <h4 className="font-bold text-gray-800 group-hover:text-purple-600 transition-colors">
                    {func.name}
                  </h4>
                </div>
                
                <p className="text-sm text-gray-600 mb-3">{func.description}</p>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">ASIS: {func.asis}%</span>
                    <span className="text-gray-500">Target: {func.target}%</span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-2">
                    <div className="relative h-full">
                      <div
                        className="absolute h-full bg-gray-400"
                        style={{ width: `${func.asis}%` }}
                      />
                      <div
                        className="absolute h-full rounded-full"
                        style={{ 
                          width: `${func.target}%`,
                          backgroundColor: func.color
                        }}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    )
  },
  {
    id: 'bz-approach',
    title: 'L\'Approche BZ : IA au Service de l\'Humain',
    subtitle: 'Notre philosophie et nos engagements éthiques',
    icon: Users,
    color: '#27AE60',
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h3 className="text-3xl font-bold text-bz-dark mb-4">
            "L'IA n'est pas là pour remplacer l'intelligence humaine, mais pour l'amplifier"
          </h3>
          <p className="text-xl text-gray-600 italic">- Vision BZ</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Contrôle Humain */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border-l-4 border-green-500">
            <div className="flex items-center gap-4 mb-4">
              <Users className="w-8 h-8 text-green-600" />
              <h4 className="text-xl font-bold text-green-800">Contrôle Humain</h4>
            </div>
            <p className="text-gray-700 mb-4">
              <strong>"L'IA propose, l'humain dispose. Toujours une validation requise."</strong>
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• 100% des décisions finales sont humaines</li>
              <li>• L'IA est un expert conseil, pas un décideur</li>
              <li>• Validation obligatoire pour toutes les recommandations</li>
              <li>• Escalade automatique vers les experts</li>
            </ul>
          </div>

          {/* Audit Complet */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border-l-4 border-blue-500">
            <div className="flex items-center gap-4 mb-4">
              <Eye className="w-8 h-8 text-blue-600" />
              <h4 className="text-xl font-bold text-blue-800">Audit Complet</h4>
            </div>
            <p className="text-gray-700 mb-4">
              <strong>"Traçabilité de toutes les interactions IA pour conformité et amélioration."</strong>
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Journalisation complète des interactions</li>
              <li>• Traçabilité des décisions IA</li>
              <li>• Conformité RGPD/LPRPDE</li>
              <li>• Amélioration continue basée sur les données</li>
            </ul>
          </div>

          {/* Sécurité & Éthique */}
          <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl border-l-4 border-red-500">
            <div className="flex items-center gap-4 mb-4">
              <Shield className="w-8 h-8 text-red-600" />
              <h4 className="text-xl font-bold text-red-800">Sécurité & Approche Éthique</h4>
            </div>
            <p className="text-gray-700 mb-4">
              <strong>"0 Incidents sécurité grâce à une approche éthique intégrée."</strong>
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Aucune donnée sensible exposée</li>
              <li>• Conformité NIST AI Framework</li>
              <li>• Chiffrement de bout en bout</li>
              <li>• Environnements sécurisés</li>
            </ul>
          </div>

          {/* Innovation & Satisfaction */}
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-xl border-l-4 border-yellow-500">
            <div className="flex items-center gap-4 mb-4">
              <TrendingUp className="w-8 h-8 text-yellow-600" />
              <h4 className="text-xl font-bold text-yellow-800">Innovation & Satisfaction</h4>
            </div>
            <p className="text-gray-700 mb-4">
              <strong>"100% Clients satisfaits avec assistance IA."</strong>
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Formation accélérée des équipes</li>
              <li>• Innovation continue</li>
              <li>• ROI centric</li>
              <li>• Décisions 65% plus rapides</li>
            </ul>
          </div>
        </div>

        {/* Métriques BZ */}
        <div className="bg-gradient-to-r from-bz-dark to-bz-blue text-white p-8 rounded-xl">
          <h4 className="text-2xl font-bold mb-6 text-center">🚀 BZ Inc. : Pionniers de l'IA Éthique</h4>
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">2024</div>
              <div className="text-sm opacity-90">Première génération</div>
              <div className="text-sm opacity-90">IA dans toutes nos apps</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">100%</div>
              <div className="text-sm opacity-90">Clients satisfaits</div>
              <div className="text-sm opacity-90">Avec assistance IA</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">0</div>
              <div className="text-sm opacity-90">Incidents sécurité</div>
              <div className="text-sm opacity-90">Approche éthique</div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'requirements',
    title: 'Exigences BZ pour l\'IA Responsable',
    subtitle: 'Nos standards de développement et de déploiement',
    icon: Target,
    color: '#E74C3C',
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h3 className="text-3xl font-bold text-bz-dark mb-4">
            Nos Engagements pour une IA de Confiance
          </h3>
          <p className="text-xl text-gray-600">Standards élevés pour garantir l'excellence éthique</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Human-Centric Design */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-green-600" />
              <h4 className="text-lg font-bold text-gray-800">Human-Centric Design</h4>
            </div>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• <strong>Validation Humaine Requise:</strong> Chaque suggestion IA est soumise à l'approbation d'un expert</li>
              <li>• <strong>Amplification, pas Remplacement:</strong> L'IA est un outil d'aide à la décision</li>
              <li>• <strong>Interfaces Intuitives:</strong> Faciliter l'interaction et la compréhension</li>
              <li>• <strong>Formation Continue:</strong> Accompagnement des équipes sur l'utilisation IA</li>
            </ul>
          </div>

          {/* Transparence et Explicabilité */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-blue-600" />
              <h4 className="text-lg font-bold text-gray-800">Transparence et Explicabilité</h4>
            </div>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• <strong>Traçabilité Complète:</strong> Enregistrement de toutes les interactions</li>
              <li>• <strong>Explicabilité des Modèles:</strong> Capacité de l'IA à justifier ses recommandations</li>
              <li>• <strong>Cadre NIST AI:</strong> Utilisation du cadre NIST AI pour la conformité</li>
              <li>• <strong>Documentation Client:</strong> Communication claire sur l'utilisation IA</li>
            </ul>
          </div>

          {/* Sécurité et Robustesse */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-red-600" />
              <h4 className="text-lg font-bold text-gray-800">Sécurité et Robustesse</h4>
            </div>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• <strong>Protection des Données:</strong> Aucune donnée sensible exposée aux plateformes externes</li>
              <li>• <strong>Détection d'Anomalies:</strong> Surveillance et réaction rapide aux anomalies</li>
              <li>• <strong>Conformité Réglementaire:</strong> Adhésion stricte aux normes de sécurité</li>
              <li>• <strong>Tests de Robustesse:</strong> Validation continue des performances</li>
            </ul>
          </div>

          {/* Équité et Responsabilité */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-6 h-6 text-yellow-600" />
              <h4 className="text-lg font-bold text-gray-800">Équité et Responsabilité</h4>
            </div>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• <strong>Évaluation des Biais:</strong> Processus réguliers pour identifier et atténuer les biais</li>
              <li>• <strong>Gouvernance Claire:</strong> Définition des rôles et responsabilités (RACI)</li>
              <li>• <strong>ROI Éthique:</strong> Focus sur le retour sur investissement client</li>
              <li>• <strong>Audits Indépendants:</strong> Évaluation objective par des tiers</li>
            </ul>
          </div>
        </div>

        {/* Avantages Concrets */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl">
          <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-3">
            <Zap className="w-6 h-6 text-blue-600" />
            Avantages Concrets de l'Approche BZ
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">65%</div>
              <div className="text-sm text-gray-600">Décisions plus rapides</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">80%</div>
              <div className="text-sm text-gray-600">Erreurs réduites</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 mb-1">100%</div>
              <div className="text-sm text-gray-600">Formation accélérée</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 mb-1">∞</div>
              <div className="text-sm text-gray-600">Innovation continue</div>
            </div>
          </div>
        </div>
      </div>
    )
  }
];

export default function AIEthiquePresentation({ isOpen, onClose }: AIEthiquePresentationProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedFunction, setSelectedFunction] = useState<AIEthicsFunction | null>(null);
  
  const slides = createSlides(setSelectedFunction);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Main Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div 
                className="p-6 text-white"
                style={{ backgroundColor: currentSlideData.color }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <currentSlideData.icon className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-bold">{currentSlideData.title}</h2>
                      {currentSlideData.subtitle && (
                        <p className="text-white/90">{currentSlideData.subtitle}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Slide {currentSlide + 1} sur {slides.length}</span>
                    <span>{Math.round(((currentSlide + 1) / slides.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                      transition={{ duration: 0.3 }}
                      className="bg-white h-2 rounded-full"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentSlideData.content}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                
                <div className="flex gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-bz-blue' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-bz-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Function Detail Modal */}
          <AnimatePresence>
            {selectedFunction && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                onClick={() => setSelectedFunction(null)}
              >
                <div className="absolute inset-0 bg-black/60" />
                <motion.div
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                  onClick={(e) => e.stopPropagation()}
                  className="relative bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[80vh] overflow-y-auto"
                >
                  {/* Function Header */}
                  <div
                    className="p-6 text-white rounded-t-xl"
                    style={{ backgroundColor: selectedFunction.color }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-4">
                        <selectedFunction.icon className="w-8 h-8" />
                        <div>
                          <h3 className="text-2xl font-bold">{selectedFunction.name}</h3>
                          <p className="text-white/90">{selectedFunction.description}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedFunction(null)}
                        className="text-white hover:bg-white/10 rounded-full p-2"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Function Content */}
                  <div className="p-6">
                    {/* Spider Chart for Function */}
                    <div className="mb-8">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">
                        🕸️ Spider Chart - {selectedFunction.name}
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="h-80 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={selectedFunction.subcategories.map(sub => ({
                              subcategory: sub.name.length > 15 ? sub.name.substring(0, 15) + '...' : sub.name,
                              asis: sub.asis,
                              target: sub.target,
                              minRequired: 60
                            }))}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="subcategory" />
                              <PolarRadiusAxis angle={90} domain={[0, 100]} />
                              <Radar
                                name="ASIS"
                                dataKey="asis"
                                stroke={selectedFunction.color}
                                fill={selectedFunction.color}
                                fillOpacity={0.3}
                                strokeWidth={2}
                              />
                              <Radar
                                name="Target"
                                dataKey="target"
                                stroke="#27AE60"
                                fill="#27AE60"
                                fillOpacity={0.2}
                                strokeWidth={2}
                              />
                              <Radar
                                name="Minimum"
                                dataKey="minRequired"
                                stroke="#E74C3C"
                                fill="none"
                                strokeWidth={1}
                                strokeDasharray="5 5"
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="flex justify-center gap-6 mt-4 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: selectedFunction.color }}></div>
                            <span>ASIS</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-500 rounded"></div>
                            <span>Target</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 border border-red-500 rounded"></div>
                            <span>Minimum (60%)</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Subcategories */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">
                        Sous-catégories ({selectedFunction.subcategories.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedFunction.subcategories.map((sub, index) => (
                          <motion.div
                            key={sub.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <h5 className="font-bold text-gray-800">{sub.name}</h5>
                              <div className="text-right">
                                <div className="text-xs text-gray-500">ASIS: {sub.asis}%</div>
                                <div className="text-xs text-gray-500">Target: {sub.target}%</div>
                              </div>
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-3">{sub.description}</p>
                            
                            <div className="space-y-2">
                              <div className="bg-gray-200 rounded-full h-2">
                                <div className="relative h-full">
                                  <div
                                    className="absolute h-full bg-gray-400"
                                    style={{ width: `${sub.asis}%` }}
                                  />
                                  <div
                                    className="absolute h-full rounded-full"
                                    style={{
                                      width: `${sub.target}%`,
                                      backgroundColor: selectedFunction.color
                                    }}
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <h6 className="text-xs font-medium text-gray-700 mb-1">Outcomes attendus:</h6>
                                <ul className="text-xs text-gray-600 space-y-1">
                                  {sub.outcomes.map((outcome, idx) => (
                                    <li key={idx} className="flex items-center gap-1">
                                      <CheckCircle className="w-3 h-3 text-green-500" />
                                      {outcome}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}
